import { supabase } from './supabase'
import type { User } from '@supabase/supabase-js'

// Define return type for auth operations
export interface AuthResult<T = User> {
  data: T | null
  error: string | null
}

/**
 * Authentication service for user signup, login, logout, and password reset
 */
export const authService = {
  /**
   * Sign up a new user with email and password
   */
  async signUp({
    email,
    password,
    username,
  }: {
    email: string
    password: string
    username?: string
  }): Promise<AuthResult<User>> {
    try {
      // First, sign up the user with Supabase Auth
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username,
          },
        },
      })

      if (error) {
        console.error('Supabase Auth signup error:', error.message)
        return { data: null, error: error.message }
      }

      // Create profile for the user if signup was successful
      if (data.user) {
        const { error: profileError } = await supabase.from('profiles').insert({
          id: data.user.id,
          username: username || email.split('@')[0],
          email,
        })

        if (profileError) {
          console.error('Profile creation error:', profileError.message)
          return { data: null, error: profileError.message }
        }

        // Ensure we're returning the user data properly
        console.log('Signup successful, returning user:', data.user)
        return { data: data.user, error: null }
      } else {
        console.error('No user data returned from Supabase Auth')
        return { data: null, error: 'No user data returned from signup' }
      }
    } catch (error) {
      console.error('Unexpected error during signup:', error)
      return {
        data: null,
        error: error instanceof Error ? error.message : 'An unknown error occurred during signup',
      }
    }
  },

  /**
   * Sign in a user with email and password
   */
  async signIn({
    email,
    password,
  }: {
    email: string
    password: string
  }): Promise<AuthResult<User>> {
    try {
      console.log('Auth service: Attempting to sign in user:', email)

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        console.error('Auth service: Sign in error:', error.message)
        return { data: null, error: error.message }
      }

      console.log('Auth service: Sign in successful for user:', email)
      return { data: data.user, error: null }
    } catch (error) {
      console.error('Auth service: Unexpected error during sign in:', error)
      return {
        data: null,
        error: error instanceof Error ? error.message : 'An unknown error occurred during sign in',
      }
    }
  },

  /**
   * Sign out the current user
   */
  async signOut(): Promise<boolean> {
    try {
      console.log('Auth service: Signing out user')
      // Use the more reliable function from our supabase service
      const { signOutAndClearSession } = await import('./supabase')
      return await signOutAndClearSession()
    } catch (error) {
      console.error('Auth service: Error during sign out:', error)
      return false
    }
  },

  /**
   * Reset password for a user
   */
  async resetPassword(email: string): Promise<AuthResult<null>> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) {
        return { data: null, error: error.message }
      }

      return { data: null, error: null }
    } catch (error) {
      return {
        data: null,
        error:
          error instanceof Error
            ? error.message
            : 'An unknown error occurred during password reset',
      }
    }
  },

  /**
   * Update password for the current user
   */
  async updatePassword(password: string): Promise<AuthResult<User>> {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password,
      })

      if (error) {
        return { data: null, error: error.message }
      }

      return { data: data.user, error: null }
    } catch (error) {
      return {
        data: null,
        error:
          error instanceof Error
            ? error.message
            : 'An unknown error occurred while updating password',
      }
    }
  },

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<AuthResult<null>> {
    try {
      // Supabase handles email verification automatically via URL
      // This method is for additional verification logic if needed
      const { error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: 'email',
      })

      if (error) {
        return { data: null, error: error.message }
      }

      return { data: null, error: null }
    } catch (error) {
      return {
        data: null,
        error:
          error instanceof Error
            ? error.message
            : 'An unknown error occurred during email verification',
      }
    }
  },

  /**
   * Resend verification email to the current user
   */
  async resendVerificationEmail(): Promise<AuthResult<null>> {
    try {
      const { data: sessionData } = await supabase.auth.getSession()

      if (!sessionData.session?.user?.email) {
        return { data: null, error: 'No user is currently logged in' }
      }

      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: sessionData.session.user.email,
      })

      if (error) {
        return { data: null, error: error.message }
      }

      return { data: null, error: null }
    } catch (error) {
      return {
        data: null,
        error:
          error instanceof Error
            ? error.message
            : 'An unknown error occurred while resending verification email',
      }
    }
  },

  /**
   * Get the current user
   */
  async getCurrentUser(): Promise<AuthResult<User>> {
    try {
      const { data, error } = await supabase.auth.getUser()

      if (error) {
        return { data: null, error: error.message }
      }

      return { data: data.user, error: null }
    } catch (error) {
      return {
        data: null,
        error:
          error instanceof Error ? error.message : 'An unknown error occurred getting current user',
      }
    }
  },

  /**
   * Update the current user's email
   */
  async updateEmail(email: string): Promise<AuthResult<null>> {
    try {
      const { error } = await supabase.auth.updateUser({ email })

      if (error) {
        return { data: null, error: error.message }
      }

      return { data: null, error: null }
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'An unknown error occurred updating email',
      }
    }
  },
}
